import { RichTextField, isFilled } from "@prismicio/client";
import { useI18N } from "@sundae/react-hooks";
import { Button, Dialog, Image } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, Suspense, lazy, memo, useCallback, useEffect } from "react";

import { PrismicRichText } from "@prismicio/react";
import { testIds } from "../../../constants/testIds.constants";
import {
  LocalControlCenterActions,
  useControlCenterContext,
} from "../../../stores/ControlCenterContext";
import { NewsActions, useNewsContext } from "../../../stores/NewsContext";
import { featureIsEnabled } from "../../../utils/features.utils";
import LoadingComponent from "../../LoadingComponent";

const LazyReactPlayer = lazy(() => import("react-player"));

/**
 * ActiveNewsDialog is a component that displays active news items in a dialog box.
 * The dialog box can be closed manually by the user by clicking a button, or automatically
 * by the system when the page is refreshed or closed.
 */
const ActiveNewsDialog: FC = () => {
  const { t } = useI18N("news");
  const {
    dispatch,
    state: { activeNewsItem, setActiveNewsItem, setIsPopoverOpen },
  } = useNewsContext();
  const {
    dispatchers: { localDispatch: localControlCenterDispatch },
  } = useControlCenterContext();

  const handleCloseDialog = useCallback(() => {
    if (featureIsEnabled("controlCenter")) {
      LocalControlCenterActions.news.setLastClosedNewsId(
        activeNewsItem?.id ?? "",
        localControlCenterDispatch
      );
      LocalControlCenterActions.showToolbar(true, localControlCenterDispatch);
      LocalControlCenterActions.showGuides(false, localControlCenterDispatch);
      LocalControlCenterActions.showHelpCenter(false, localControlCenterDispatch);
      LocalControlCenterActions.showInbox(true, localControlCenterDispatch);
    } else {
      setIsPopoverOpen(true);
    }

    setActiveNewsItem(undefined);
  }, [
    activeNewsItem?.id,
    dispatch,
    localControlCenterDispatch,
    setActiveNewsItem,
    setIsPopoverOpen,
  ]);

  /**
   * Mark the active news item as read when the dialog is opened.
   * This effect runs whenever the active news item changes.
   * It ensures to update the read state even when the dialog is forced to open.
   */
  useEffect(() => {
    if (activeNewsItem) {
      NewsActions.setReadNewsItem(activeNewsItem.id, dispatch);
    }
  }, [activeNewsItem, dispatch]);

  if (activeNewsItem) {
    return (
      <Dialog
        closeDialog={handleCloseDialog}
        open={Boolean(activeNewsItem)}
        description={`${activeNewsItem.data.desc}`}
        title={activeNewsItem.data.title}
        withBlurryCorner
        testId={testIds.activeNewsDialog.content}
      >
        <div className="grid gap-2">
          {activeNewsItem.data.dialog_image?.url && (
            <Image
              src={activeNewsItem.data.dialog_image.url}
              alt={activeNewsItem.data.dialog_image.alt ?? "Sundae Labs"}
              width={activeNewsItem.data.dialog_image.dimensions.width}
              height={activeNewsItem.data.dialog_image.dimensions.height}
            />
          )}

          {activeNewsItem.data.slices1?.length ? (
            <div
              className={cx("grid gap-2", {
                "mt-4": isFilled.image(activeNewsItem.data.dialog_image),
              })}
            >
              {activeNewsItem.data.slices1.map((slice) => {
                return (
                  <div key={slice.id}>
                    {slice.slice_type === "text_block" && (
                      <PrismicRichText key={slice.id} field={slice.primary.copy as RichTextField} />
                    )}

                    {slice.slice_type === "youtube" && (
                      <div className="relative h-0 w-full overflow-hidden pb-[56.25%]">
                        <Suspense fallback={<LoadingComponent />}>
                          <LazyReactPlayer
                            className="absolute left-0 top-0 !h-full !w-full"
                            url={(slice.primary.youtube_url as { embed_url: string }).embed_url}
                          />
                        </Suspense>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}

          <div
            className={cx("flex items-center justify-center", {
              "mt-2": activeNewsItem.data.slices1?.length,
            })}
          >
            <Button onClick={handleCloseDialog} variant="primary">
              {activeNewsItem.data.dismiss_button_cta ?? t("dismissButton")}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return null;
};

export default memo(ActiveNewsDialog);
